/**
 * FinanceQuoterContainer.tsx
 * 
 * Container component that wraps FinanceQuoter with Redux state management,
 * business rules engine, and dialog handling.
 * 
 * This container handles:
 * - All Redux selectors (editingApp, financeQuoteIsDirty, loggedInUser, states, errors)
 * - All Redux dispatches (updateEditingApp, updateFinanceQuoteCalcErrorsSuccess)
 * - Business rules engine integration
 * - Dialog provider for modals
 * 
 * The wrapped FinanceQuoter component (and its children) will be "dumb" components
 * that receive all state and callbacks as props, making them suitable for a shared library.
 */

import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import produce from "immer";
import {
  FinanceLoanClass,
  FinanceQuote,
  FinanceQuoteFee,
  usesFinanceQuote,
  FinanceQuoteTax, 
  updateFinanceQuoteTax,
  updateFinanceQuoteFee,
  hasValue
} from "@amsanalytics/react-shared-library";
import { IApplication } from "../../../Interfaces/Application/IApplication";
import { IConfiguration } from "../../../Data/ConfigurationData";
import { ILookupItems } from "../../../Interfaces/ILookupItems";
import { TabName } from "../../Views/OrderEditor/OrderEditorTabs";
import { ApplicationDispatch, updateFinanceQuotes as solveFinanceQuotes } from "../../../store/application/types";
import { useBusinessRulesEngine } from "../../../Data/BusinessRulesData";
import { AppState } from "../../../store/rootReducer";
import { useDialog } from "../../../providers/DialogProvider";
import { IState } from "../../../Interfaces/Common/IState";
import { updateEditingApp } from "../../../store/application/actions";
import { updateFinanceQuoteCalcErrorsSuccess } from "../../../store/Notifications/actions";
import {
  getDefaultFinanceQuotes,
  getSelectedFinanceQuote,
  handleChangeSelectedFinanceLoanClass,
  handleUpdateSelectedQuote,
  handleUpdateSingleQuoteField,
  isIndirectConvertedToFinance,
  updateFinanceQuotes
} from "../../../utils/quoteUtils";
import {
  calculateDocStampAmount,
  getFinanceQuoteFee,
  handleChangeOfPrimaryApplicantsPrimaryZipCode,
  handleUpdateDocStampQuoteFee,
  handleUpdateFinanceQuoteFee,
  getFinanceQuoteValues
} from "../../../utils/LoanCalcUtils";
import { useFinanceQuoteSolve } from "../../../hooks/UseFinanceQuoteSolve";
import FinanceQuoter from './FinanceQuoter';

export interface FinanceQuoterContainerProps {
  financeLoanClasses: FinanceLoanClass[];
  configuration: IConfiguration;
  lookups: ILookupItems;
  preventDesking: boolean;
  navigate: (tabName: TabName) => void;
}

/**
 * Container component that provides Redux state and dispatch to FinanceQuoter.
 * This allows FinanceQuoter to be a "dumb" component that can be moved to a shared library.
 */
export default function FinanceQuoterContainer(props: FinanceQuoterContainerProps) {
  const dispatch = useDispatch<ApplicationDispatch>();
  const businessRulesEngine = useBusinessRulesEngine();
  const [openDialog, closeDialog] = useDialog();
  const [handleQuoteSolve] = useFinanceQuoteSolve();

  // Redux selectors - all state needed by FinanceQuoter and its children
  const currentApp = useSelector((state: AppState) => state.applications.editingApp!);
  const financeQuoteIsDirty = useSelector((state: AppState) => state.applications.financeQuoteIsDirty!);
  const activeUser = useSelector((state: AppState) => state.applications.loggedInUser!.employee);
  const applicationFinanceErrors = useSelector((state: AppState) => state.notifications.financeQuoteErrors);
  const financeQuoteCalcErrors = useSelector((state: AppState) => state.notifications.financeQuoteCalcErrors);
  const states: IState[] = useSelector((state: AppState) => state.applications.states);
  
  // Additional selectors needed by child components
  const employees = useSelector((state: AppState) => state.applications.employees);
  const quotePayTypes = useSelector((state: AppState) => state.applications.quotePayTypes);
  const applicationDownPaymentResponses = useSelector((state: AppState) => state.applications.applicationDownPaymentResponses);

  // Derived state
  const selectedFinanceQuote = getSelectedFinanceQuote(currentApp);
  const selectedFinanceLoanClass = props.financeLoanClasses.find(flc => flc.id === selectedFinanceQuote?.financeLoanClassId);

  // Callback: Update app with business rules
  const handleUpdateApp = useCallback((updatedApp: IApplication) => {
    dispatch(updateEditingApp(updatedApp, businessRulesEngine));
  }, [dispatch, businessRulesEngine]);

  // Callback: Update single quote field
  const handleUpdateSingleQuoteFieldCallback = useCallback(<K extends keyof FinanceQuote>(
    fieldName: K,
    value: FinanceQuote[K],
    financeQuoteId?: string
  ) => {
    if (selectedFinanceQuote) {
      handleUpdateSingleQuoteField(
        fieldName,
        value,
        financeQuoteId ?? selectedFinanceQuote.id,
        currentApp,
        selectedFinanceLoanClass!,
        props.lookups.financeWarranties,
        dispatch,
        businessRulesEngine
      );
    }
  }, [selectedFinanceQuote, currentApp, selectedFinanceLoanClass, props.lookups.financeWarranties, dispatch, businessRulesEngine]);

  // Callback: Update selected quote
  const handleUpdateSelectedQuoteCallback = useCallback((selectedFinanceQuoteId: string) => {
    handleUpdateSelectedQuote(
      selectedFinanceQuoteId,
      currentApp,
      dispatch,
      businessRulesEngine
    );
  }, [currentApp, dispatch, businessRulesEngine]);

  // Callback: Update tax
  const handleUpdateTax = useCallback((updatedTax: FinanceQuoteTax): void => {
    const updatedApp = produce(currentApp!, updatedCurrentApp => {
      updatedCurrentApp.financeQuotes.forEach(financeQuote => {
        updateFinanceQuoteTax(financeQuote, updatedTax);
      });
    });
    dispatch(updateEditingApp(updatedApp, businessRulesEngine));
  }, [currentApp, dispatch, businessRulesEngine]);

  // Callback: Update fee
  const handleUpdateFee = useCallback((updatedFee: FinanceQuoteFee): void => {
    handleUpdateFinanceQuoteFee(currentApp!, updatedFee, dispatch, businessRulesEngine);
  }, [currentApp, dispatch, businessRulesEngine]);

  // Callback: Clear calc errors
  const handleClearCalcErrors = useCallback(() => {
    dispatch(updateFinanceQuoteCalcErrorsSuccess([]) as any);
  }, [dispatch]);

  // Callback: Initialize default quotes
  const handleInitializeDefaultQuotes = useCallback(async () => {
    updateFinanceQuotes(
      currentApp,
      await getDefaultFinanceQuotes(
        currentApp,
        props.configuration,
        activeUser!,
        props.financeLoanClasses,
        dispatch
      ),
      dispatch,
      businessRulesEngine
    );
  }, [currentApp, props.configuration, activeUser, props.financeLoanClasses, dispatch, businessRulesEngine]);

  // Callback: Handle zip code change
  const handleZipCodeChange = useCallback(async () => {
    if (selectedFinanceQuote && selectedFinanceLoanClass) {
      await handleChangeOfPrimaryApplicantsPrimaryZipCode(currentApp, selectedFinanceLoanClass, dispatch, businessRulesEngine);
    }
  }, [currentApp, selectedFinanceQuote, selectedFinanceLoanClass, dispatch, businessRulesEngine]);

  // Callback: Handle doc stamp fee update
  const handleDocStampFeeUpdate = useCallback(() => {
    if (selectedFinanceLoanClass) {
      const financeQuoteValues = getFinanceQuoteValues(selectedFinanceQuote, selectedFinanceLoanClass, states);
      handleUpdateDocStampQuoteFee(selectedFinanceLoanClass, currentApp, financeQuoteValues, dispatch, businessRulesEngine);
    }
  }, [selectedFinanceLoanClass, selectedFinanceQuote, states, currentApp, dispatch, businessRulesEngine]);

  // Callback: Handle loan class change
  const handleLoanClassChange = useCallback((loanClass: FinanceLoanClass) => {
    handleChangeSelectedFinanceLoanClass(currentApp, loanClass, dispatch, businessRulesEngine);
  }, [currentApp, dispatch, businessRulesEngine]);

  // Callback: Update customer addon
  const handleUpdateCustomerAddon = useCallback((customerId: string, fieldName: string, value: any): void => {
    const selectedFinanceQuote = getSelectedFinanceQuote(currentApp);
    if (!selectedFinanceQuote) return;
    const updatedApp = produce(currentApp, draft => {
      const quote = draft.financeQuotes.find(fq => fq.id === selectedFinanceQuote.id);
      if (quote) {
        const customerDetail = quote.financeQuoteCustomerDetails.find(cd => cd.customerId === customerId);
        if (customerDetail) {
          (customerDetail as any)[fieldName] = value;
        }
      }
    });
    dispatch(updateEditingApp(updatedApp, businessRulesEngine));
  }, [currentApp, dispatch, businessRulesEngine]);

  // Auto-sync trade-in vehicle values to finance quotes and re-solve when they change.
  // Tracks previous trade-in vehicle values via ref so we can detect vehicle-side changes
  // even when the dialog callback has already synced the values to the quotes.
  const prevTradeInRef = useRef<{ tradeValue: number; tradeBalance: number } | null>(null);
  
  useEffect(() => {
    // An indirect application keeps its Indirect quote type after move-forward. We still treat it as finance-mode UI
    // once the underwriter quote is marked converted and at least one finance quote exists.
    const isIndirectConvertedToFinanceResult = isIndirectConvertedToFinance(currentApp, props.configuration);

    // Run this finance quote sync only for native finance apps or converted indirect apps.
    if (!usesFinanceQuote(currentApp.quoteType) && !isIndirectConvertedToFinanceResult) return;

    const nonDeletedQuotes = currentApp.financeQuotes.filter(fq => !hasValue(fq.deletedDate));
    if (nonDeletedQuotes.length === 0) return;

    const tradeIn = currentApp.tradeInVehicle;
    const isTradeInRemoved = !tradeIn || hasValue(tradeIn.deletedDate);
    const tradeIn2 = currentApp.tradeInVehicle2;
    const isTradeIn2Removed = !tradeIn2 || hasValue(tradeIn2.deletedDate);
    const expectedTradeValue = (isTradeInRemoved ? 0 : tradeIn.tradeValue) + (isTradeIn2Removed ? 0 : tradeIn2.tradeValue);
    const expectedTradeBalance = (isTradeInRemoved ? 0 : tradeIn.tradeBalance) + (isTradeIn2Removed ? 0 : tradeIn2.tradeBalance);

    // Initialize ref on first render without triggering a solve
    if (prevTradeInRef.current === null) {
      prevTradeInRef.current = { tradeValue: expectedTradeValue, tradeBalance: expectedTradeBalance };
      return;
    }

    const prev = prevTradeInRef.current;
    const tradeInVehicleChanged = prev.tradeValue !== expectedTradeValue || prev.tradeBalance !== expectedTradeBalance;

    if (!tradeInVehicleChanged) return;

    // Update ref to current trade-in values
    prevTradeInRef.current = { tradeValue: expectedTradeValue, tradeBalance: expectedTradeBalance };

    // Build the app with synced trade-in values on all finance quotes
    // Also recalculate the doc stamp fee so the solve gets the correct value
    // (otherwise Carleton echoes back the old doc stamp and post-solve recalculation re-dirties the quote)
    const updatedApp = produce(currentApp, draft => {
      draft.financeQuotes.forEach(fq => {
        fq.tradeInValue = expectedTradeValue;
        fq.tradeInPayoff = expectedTradeBalance;
      });

      // Recalculate doc stamp fee with the new trade-in values before solving
      if (selectedFinanceLoanClass) {
        const selectedQuote = draft.financeQuotes.find(fq => fq.isSelected);
        if (selectedQuote) {
          const updatedFinanceQuoteValues = getFinanceQuoteValues(selectedQuote, selectedFinanceLoanClass, states);
          const newDocStampAmount = calculateDocStampAmount(selectedQuote, selectedFinanceLoanClass, updatedFinanceQuoteValues);
          if (newDocStampAmount !== null) {
            const docStampFee = getFinanceQuoteFee(selectedFinanceLoanClass, selectedQuote.financeQuoteFees, "DOC STAMP")
              ?? getFinanceQuoteFee(selectedFinanceLoanClass, selectedQuote.financeQuoteFees, "DOC STAMPS");
            if (docStampFee) {
              // Apply updated doc stamp to all quotes
              draft.financeQuotes.forEach(fq => {
                updateFinanceQuoteFee(fq, { ...docStampFee, amount: newDocStampAmount });
              });
            }
          }
        }
      }
    });

    // Re-solve if a quote has been previously calculated.
    // We dispatch the solve directly with updatedApp (instead of calling handleQuoteSolve)
    // because handleQuoteSolve reads from a stale closure and would send the old values.
    const aQuoteHasBeenCalculated = currentApp.financeQuotes.some(afq => hasValue(afq.quoteGeneratedDate));
    if (aQuoteHasBeenCalculated) {
      dispatch(solveFinanceQuotes(updatedApp, undefined));
    } else {
      dispatch(updateEditingApp(updatedApp, businessRulesEngine));
    }
  }, [
    currentApp.tradeInVehicle?.tradeValue,
    currentApp.tradeInVehicle?.tradeBalance,
    currentApp.tradeInVehicle?.deletedDate,
    currentApp.tradeInVehicle,
    currentApp.tradeInVehicle2?.tradeValue,
    currentApp.tradeInVehicle2?.tradeBalance,
    currentApp.tradeInVehicle2?.deletedDate,
    currentApp.tradeInVehicle2,
  ]);

  // Callback: Update quote addon
  const handleUpdateQuoteAddon = useCallback((fieldName: string, value: any): void => {
    const selectedFinanceQuote = getSelectedFinanceQuote(currentApp);
    if (!selectedFinanceQuote) return;
    const updatedApp = produce(currentApp, draft => {
      const quote = draft.financeQuotes.find(fq => fq.id === selectedFinanceQuote.id);
      if (quote) {
        (quote as any)[fieldName] = value;
      }
    });
    dispatch(updateEditingApp(updatedApp, businessRulesEngine));
  }, [currentApp, dispatch, businessRulesEngine]);

  return (
    <FinanceQuoter
      {...props}
      currentApp={currentApp}
      financeQuoteIsDirty={financeQuoteIsDirty}
      activeUser={activeUser}
      states={states}
      applicationFinanceErrors={applicationFinanceErrors}
      financeQuoteCalcErrors={financeQuoteCalcErrors}
      employees={employees}
      quotePayTypes={quotePayTypes}
      applicationDownPaymentResponses={applicationDownPaymentResponses ?? []}
      onUpdateApp={handleUpdateApp}
      onUpdateSingleQuoteField={handleUpdateSingleQuoteFieldCallback}
      onUpdateSelectedQuote={handleUpdateSelectedQuoteCallback}
      onUpdateTax={handleUpdateTax}
      onUpdateFee={handleUpdateFee}
      onClearCalcErrors={handleClearCalcErrors}
      onInitializeDefaultQuotes={handleInitializeDefaultQuotes}
      onZipCodeChange={handleZipCodeChange}
      onDocStampFeeUpdate={handleDocStampFeeUpdate}
      onLoanClassChange={handleLoanClassChange}
      onSolve={handleQuoteSolve}
      onUpdateCustomerAddon={handleUpdateCustomerAddon}
      onUpdateQuoteAddon={handleUpdateQuoteAddon}
      openDialog={openDialog}
      closeDialog={closeDialog}
      dispatch={dispatch}
    />
  );
}
